import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

function isEveryDay(weekdays) {
  if (!Array.isArray(weekdays) || weekdays.length !== 7) return false;
  return ALL_WEEKDAYS.every((d) => weekdays.includes(d));
}

function formatTimes(times) {
  if (!Array.isArray(times) || times.length === 0) return "—";
  return times.join(" · ");
}

function formatWeekdays(weekdays, t) {
  if (isEveryDay(weekdays)) return t("app.medications.everyDay");
  const labels = t("app.medications.weekdayShort");
  if (!Array.isArray(weekdays) || !weekdays.length) return "—";
  return weekdays
    .map((d) => labels?.[d] ?? "")
    .filter(Boolean)
    .join(" · ");
}

function formatDateRange(start, end, t) {
  if (!start && !end) return null;
  if (start && end) return `${start} → ${end}`;
  if (start) return `${t("app.medications.startsOn")} ${start}`;
  return `${t("app.medications.endsOn")} ${end}`;
}

export default function Medications() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/medications", {
        params: { include_inactive: showInactive ? "true" : "false" },
      });
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const { active, cancelled } = useMemo(() => {
    const a = [];
    const c = [];
    for (const m of items) {
      (m.active === false ? c : a).push(m);
    }
    return { active: a, cancelled: c };
  }, [items]);

  return (
    <div
      className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-5xl"
      data-testid="medications-page"
    >
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-cap">— {t("app.sidebar2.medications")}</p>
          <h1
            className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3"
            data-testid="medications-title"
          >
            {t("app.medications.title")}
          </h1>
          <p className="font-display-italic text-[var(--ink-soft)] mt-3 max-w-3xl">
            {t("app.medications.sub")}
          </p>
        </div>
        <label
          className="text-xs text-[var(--ink-faint)] tracking-monocle uppercase flex items-center gap-3 cursor-pointer select-none"
          data-testid="medications-include-inactive-label"
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="accent-[var(--oxblood)]"
            data-testid="medications-include-inactive"
          />
          {t("app.medications.includeInactive")}
        </label>
      </div>

      {loading && (
        <p
          className="mt-12 font-display-italic text-[var(--ink-soft)]"
          data-testid="medications-loading"
        >
          {t("app.medications.loading")}
        </p>
      )}

      {!loading && error && (
        <p className="text-[var(--error)] text-sm mt-12" data-testid="medications-error">
          {error}
        </p>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="mt-12 border border-[var(--border)] bg-[var(--bg)] py-12 px-6 text-center"
          data-testid="medications-empty"
        >
          <p className="font-display-italic text-[var(--ink-soft)] text-lg">
            {t("app.medications.empty")}
          </p>
          <p className="text-xs text-[var(--ink-faint)] mt-3 tracking-monocle uppercase">
            {t("app.medications.viaChatHint")}
          </p>
          <Link
            to="/app/chat"
            className="btn-ghost mt-6 inline-block"
            data-testid="medications-open-chat-empty"
          >
            {t("app.openChat")} →
          </Link>
        </div>
      )}

      {!loading && !error && active.length > 0 && (
        <section className="mt-12" data-testid="medications-active-section">
          <p className="label-cap text-[var(--oxblood)]">
            {t("app.medications.activeHeader")} · {active.length}
          </p>
          <ul className="mt-4 space-y-px bg-[var(--border)]" data-testid="medications-active-list">
            {active.map((m) => (
              <MedicationRow key={m.id} med={m} t={t} />
            ))}
          </ul>
        </section>
      )}

      {!loading && !error && cancelled.length > 0 && (
        <section className="mt-12" data-testid="medications-cancelled-section">
          <p className="label-cap text-[var(--ink-faint)]">
            {t("app.medications.cancelledHeader")} · {cancelled.length}
          </p>
          <ul className="mt-4 space-y-px bg-[var(--border)]" data-testid="medications-cancelled-list">
            {cancelled.map((m) => (
              <MedicationRow key={m.id} med={m} t={t} muted />
            ))}
          </ul>
        </section>
      )}

      {!loading && !error && items.length > 0 && (
        <p
          className="mt-12 text-xs text-[var(--ink-faint)] tracking-monocle uppercase flex items-center gap-4 flex-wrap"
          data-testid="medications-via-chat-footer"
        >
          <span>{t("app.medications.viaChatHint")}</span>
          <Link
            to="/app/chat"
            className="underline-offset-4 hover:underline text-[var(--ink-soft)]"
            data-testid="medications-open-chat-footer"
          >
            {t("app.openChat")} →
          </Link>
        </p>
      )}
    </div>
  );
}

function MedicationRow({ med, t, muted = false }) {
  const dateRange = formatDateRange(med.start_date, med.end_date, t);
  return (
    <article
      className={`bg-[var(--bg)] p-6 grid grid-cols-12 gap-4 items-baseline ${
        muted ? "opacity-60" : ""
      }`}
      data-testid={`medication-${med.id}`}
    >
      <div className="col-span-12 md:col-span-5">
        <p
          className={`font-display text-2xl leading-snug ${
            muted ? "line-through" : ""
          }`}
          data-testid={`medication-name-${med.id}`}
        >
          {med.name}
        </p>
        {med.dosage && (
          <p
            className="text-sm text-[var(--ink-soft)] mt-1"
            data-testid={`medication-dosage-${med.id}`}
          >
            {med.dosage}
          </p>
        )}
      </div>
      <div className="col-span-12 md:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div data-testid={`medication-times-${med.id}`}>
          <p className="label-cap text-[var(--ink-faint)]">
            {t("app.medications.timesLabel")}
          </p>
          <p className="font-display text-lg mt-1">{formatTimes(med.times_local)}</p>
        </div>
        <div data-testid={`medication-weekdays-${med.id}`}>
          <p className="label-cap text-[var(--ink-faint)]">
            {t("app.medications.weekdaysLabel")}
          </p>
          <p className="font-display text-lg mt-1">
            {formatWeekdays(med.weekdays, t)}
          </p>
        </div>
        <div data-testid={`medication-period-${med.id}`}>
          <p className="label-cap text-[var(--ink-faint)]">
            {t("app.medications.periodLabel")}
          </p>
          <p className="font-display text-lg mt-1">
            {dateRange || t("app.medications.ongoing")}
          </p>
        </div>
        {med.notes && (
          <p
            className="col-span-1 md:col-span-3 text-sm text-[var(--ink-soft)] mt-1 leading-relaxed"
            data-testid={`medication-notes-${med.id}`}
          >
            {med.notes}
          </p>
        )}
      </div>
    </article>
  );
}
